import { AddressFields } from '@/components/forms/CreateNewBusinessForm/Fields/AddressFields';
import { InstantClientSelect } from '@/components/selects/ClientSelect';
import { FormLabel, Grid, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useFormikContext } from 'formik';
import React, { useMemo } from 'react';

// The contact picker (shared with the email dialogs) works in
// { id, contactName, contactEmail }; task attendees are { id, email, firstName, lastName }
// (also accepting user objects, e.g. the owner added automatically by AddTaskDialog).
const toPickerContact = (attendee) => ({
	id: attendee.id ?? attendee.email,
	contactEmail: attendee.email,
	contactName:
		[attendee.firstName, attendee.lastName].filter(Boolean).join(' ') ||
		attendee.name ||
		attendee.email,
});

const toAttendee = (contact) => {
	// A typed-in email address has contactName === contactEmail — no names to split.
	const names =
		contact.contactName && contact.contactName !== contact.contactEmail
			? contact.contactName.trim().split(/\s+/)
			: [];
	return {
		id: contact.contactEmail,
		email: contact.contactEmail,
		firstName: names[0] ?? '',
		lastName: names.slice(1).join(' '),
	};
};

const MeetingFields = ({ disabled }) => {
	const formik = useFormikContext();
	const { values, setFieldValue } = formik;

	// Memoised: the picker resets its selection whenever this array's identity changes.
	const pickerAttendees = useMemo(
		() => (values.attendees ?? []).map(toPickerContact),
		[values.attendees],
	);

	return (
		<>
			{values.taskType === 'MEETING' && (
				<>
					<Grid size={12}>
						<FormLabel>{'Attendees'}</FormLabel>
						<InstantClientSelect
							showContact
							attendees={pickerAttendees}
							setClient={(contacts) => {
								setFieldValue(
									'attendees',
									(Array.isArray(contacts) ? contacts : []).map(toAttendee),
								);
							}}
							disabled={disabled}
						/>
					</Grid>
					<Grid size={12}>
						<FormLabel>{'Meeting type'}</FormLabel>
						<ToggleButtonGroup
							color='primary'
							value={values.isOnlineMeeting}
							size='small'
							exclusive
							onChange={(event, newValue) =>
								setFieldValue('isOnlineMeeting', Boolean(newValue))
							}
							disabled={disabled}
							sx={(theme) => ({
								marginLeft: 2,
								boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
								'& .MuiToggleButtonGroup-grouped': {
									border: 'none',
									margin: '4px',
									padding: '3px 12px',
									'&:not(:first-of-type)': {
										borderRadius: '5px',
									},
									'&:first-of-type': {
										borderRadius: '5px',
									},
									color: theme.palette.text.secondary,
									backgroundColor: 'transparent',
									'&.Mui-selected': {
										color: theme.palette.primary.main,
										backgroundColor: 'rgba(80, 90, 252, 0.33)',
									},
									'&:hover': {
										backgroundColor: 'rgba(80, 90, 252, 0.1)',
									},
								},
							})}
						>
							<ToggleButton value={false}>In person</ToggleButton>
							<ToggleButton value={true}>Online</ToggleButton>
						</ToggleButtonGroup>
					</Grid>
					{!values.isOnlineMeeting && (
						<Grid size={12}>
							<FormLabel required>{'Location'}</FormLabel>
							<AddressFields formik={formik} minimal={true} loading={disabled} />
						</Grid>
					)}
				</>
			)}
		</>
	);
};

export default MeetingFields;
